import { Checkbox, CheckboxGroup } from "@/components/frontend/ui/form/inputs/checkbox";
import Description from "@/components/frontend/ui/form/inputs/description";
import FieldError from "@/components/frontend/ui/form/inputs/field-error";
import { cn } from "@/lib/utils";
import { type FormCheckboxBlock as FormCheckboxBlockT } from "@/payload-types";

type FormCheckboxBlockProps = FormCheckboxBlockT & {
	containerClassName?: string;
	fieldName?: string;
	fieldValue?: unknown;
	fieldError?: string;
	onFieldChange?: (value: unknown) => void;
	onFieldBlur?: () => void;
};

export const FormCheckboxBlock: React.FC<FormCheckboxBlockProps> = ({
	content,
	options,
	settings,
	containerClassName,
	fieldName,
	fieldValue,
	fieldError,
	onFieldChange,
	onFieldBlur,
}) => {
	const c = content ?? {};
	const normalizedOptions = options ?? [];
	const defaultGroupValues = normalizedOptions
		.filter((option: { defaultChecked?: boolean | null }) => option.defaultChecked)
		.map((option: { value: string }) => option.value);

	if (c.type === "single") {
		return (
			<div className={cn("flex flex-col items-start gap-1", containerClassName)}>
				<Checkbox
					name={fieldName}
					required={c.required ?? false}
					checked={Boolean(fieldValue)}
					onCheckedChange={(checked) => onFieldChange?.(Boolean(checked))}
					onBlur={onFieldBlur}
					label={c.singleLabel}
				/>
				{c.description && <Description>{c.description}</Description>}
				<FieldError message={fieldError} />
			</div>
		);
	}

	return (
		<div className={cn("flex flex-col items-start gap-1", containerClassName)}>
			<CheckboxGroup
				name={fieldName}
				label={c.label ?? undefined}
				required={c.required ?? false}
				value={(Array.isArray(fieldValue) ? fieldValue : defaultGroupValues) as string[]}
				onValueChange={(value) => onFieldChange?.(value)}
				onBlur={onFieldBlur}
				className="w-full"
				aria-required={c.required ?? false}
				checkBoxesContainerClassName={settings?.groupDirection === "horizontal" ? "flex-row flex-wrap gap-x-4 gap-y-2" : undefined}
			>
				{normalizedOptions.map((option: { label: string; value: string }) => (
					<Checkbox key={option.value} label={option.label} value={option.value} />
				))}
			</CheckboxGroup>
			{c.description && <Description>{c.description}</Description>}
			<FieldError message={fieldError} />
		</div>
	);
};
